import React, { useState, useEffect, useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import CalendarToday from '@mui/icons-material/CalendarToday'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import PhoneAndroid from '@mui/icons-material/PhoneAndroid'
import MailOutline from '@mui/icons-material/MailOutline'
import NumbersIcon from '@mui/icons-material/Numbers'
import PermIdentity from '@mui/icons-material/PermIdentity'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import WcIcon from '@mui/icons-material/Wc'
import LanguageIcon from '@mui/icons-material/Language'
import DevicesIcon from '@mui/icons-material/Devices'
import LoginIcon from '@mui/icons-material/Login'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import PaymentIcon from '@mui/icons-material/Payment'
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun'
import LinkIcon from '@mui/icons-material/Link'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import EmbeddedTableContainer from '../../component/EmbeddedTableContainer'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import { formatDate, renderDateTime } from '../../utils/dateDisplay'
import { formatRunDuration, formatRunPace } from '../../utils/runDisplay'
import { getLastLoginDeviceLabel, getSignupPathLabel, getSignupProviderLabel } from '../../utils/userDisplay'

const DETAIL_CARD_SX = {
    p: { xs: 2, md: 2.5 },
    height: '100%',
    borderColor: 'divider',
    borderRadius: 2,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
}

const DETAIL_FIELD_COLUMN_SX = {
    display: 'grid',
    gap: 1.25,
    alignContent: 'start',
}

const DETAIL_ICON_SX = {
    mt: '2px',
    fontSize: 18,
    color: 'text.secondary',
    flexShrink: 0,
}

const TEMP_PASSWORD_REASON_MAX_LENGTH = 255
const NICKNAME_MODERATION_REASON_MAX_LENGTH = 255

function DetailSectionTitle({ children, icon }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {icon}
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {children}
            </Typography>
        </Box>
    )
}

function DetailField({ icon, label, value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
            {icon}
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 600, wordBreak: 'break-all' }}>
                    {value || '-'}
                </Typography>
            </Box>
        </Box>
    )
}

function RunSummaryCard({ label, value }) {
    return (
        <Box
            sx={{
                p: 1.25,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'action.hover',
                minWidth: 0,
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {label}
            </Typography>
            <Typography variant="subtitle2" sx={{ mt: 0.5, fontWeight: 800, wordBreak: 'break-all' }}>
                {value || '-'}
            </Typography>
        </Box>
    )
}

export default function AppUser(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.userId
    const role = Number(props.role || 0)
    const searchText = locationState.searchText
    const searchSelect = locationState.searchSelect
    const listSearch = location.search || ''
    const [loading, setLoading] = useState(true)
    const [hasDetail, setHasDetail] = useState(false)
    const [user, setUser] = useState({})
    const [paymentSummary, setPaymentSummary] = useState({ paymentCount: 0, lastPaymentAt: null })
    const [snsLinks, setSnsLinks] = useState([])
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [temporaryPasswordDialogOpen, setTemporaryPasswordDialogOpen] = useState(false)
    const [temporaryPasswordReason, setTemporaryPasswordReason] = useState('')
    const [temporaryPassword, setTemporaryPassword] = useState('')
    const [temporaryPasswordLoading, setTemporaryPasswordLoading] = useState(false)
    const [temporaryPasswordCopied, setTemporaryPasswordCopied] = useState(false)
    const [nicknameModerationDialogOpen, setNicknameModerationDialogOpen] = useState(false)
    const [nicknameModerationReason, setNicknameModerationReason] = useState('')
    const [nicknameModerationLoading, setNicknameModerationLoading] = useState(false)
    const [nicknameNoticeResending, setNicknameNoticeResending] = useState(false)
    const [nicknameModeration, setNicknameModeration] = useState(null)
    const canIssueTemporaryPassword = role >= 5
    const canModerateNickname = role >= 4

    const moveToList = useCallback(() => {
        history.push(createListLinkTarget({
            pathname: '/users',
            search: listSearch,
            curPage: locationState.curPage,
            searchText,
            searchSelect,
        }))
    }, [history, listSearch, locationState.curPage, searchSelect, searchText])
    
    const getData = useCallback(async () => {
        if (!id) {
            const errorMessage = '필수 파라미터가 없어 회원 상세를 불러올 수 없습니다.'
            setUser({})
            setPaymentSummary({ paymentCount: 0, lastPaymentAt: null })
            setSnsLinks([])
            setNicknameModeration(null)
            setHasDetail(false)
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            setLoading(false)
            return
        }

        setLoading(true)
        setHasDetail(false)
        setDetailErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appUser/getDetail', { userId: id })
            // console.log('data: ', data)
            if (data.code === 200) {
                const detailData = data.data || {}
                const userData = detailData.userData || {}

                if (!userData.userId) {
                    setUser({})
                    setPaymentSummary({ paymentCount: 0, lastPaymentAt: null })
                    setSnsLinks([])
                    setNicknameModeration(null)
                    setDetailErrorMessage('회원 상세 정보를 찾을 수 없습니다.')
                    setLoading(false)
                    return
                }

                setUser(userData)
                setPaymentSummary(detailData.paymentSummary || { paymentCount: 0, lastPaymentAt: null })
                setSnsLinks(detailData.snsLinks || [])
                setNicknameModeration(detailData.nicknameModeration || null)
                setHasDetail(true)
                setDetailErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '회원 상세를 불러오지 못했습니다.'
                setUser({})
                setPaymentSummary({ paymentCount: 0, lastPaymentAt: null })
                setSnsLinks([])
                setNicknameModeration(null)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '회원 상세를 불러오지 못했습니다.'
            setUser({})
            setPaymentSummary({ paymentCount: 0, lastPaymentAt: null })
            setSnsLinks([])
            setNicknameModeration(null)
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [history, id])

    useEffect(() => {
        getData()
    }, [getData])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const openTemporaryPasswordDialog = () => {
        setTemporaryPasswordReason('')
        setTemporaryPassword('')
        setTemporaryPasswordCopied(false)
        setTemporaryPasswordDialogOpen(true)
    }

    const closeTemporaryPasswordDialog = () => {
        if (temporaryPasswordLoading) {
            return
        }

        setTemporaryPasswordDialogOpen(false)
        setTemporaryPasswordReason('')
        setTemporaryPassword('')
        setTemporaryPasswordCopied(false)
    }

    const copyTextToClipboard = async (text) => {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return
        }

        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.left = '-9999px'
        document.body.appendChild(textarea)
        textarea.select()
        const copied = document.execCommand('copy')
        document.body.removeChild(textarea)

        if (!copied) {
            throw new Error('copy_failed')
        }
    }

    const copyTemporaryPassword = async () => {
        try {
            await copyTextToClipboard(temporaryPassword)
            setTemporaryPasswordCopied(true)
            setOpenSnackbar(true)
            setSeveritySnackbar('success')
            setMessageSnackbar('임시 비밀번호를 복사했습니다.')
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('임시 비밀번호 복사에 실패했습니다.')
        }
    }

    const issueTemporaryPassword = async () => {
        const reason = temporaryPasswordReason.trim()

        if (!reason) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('처리 사유를 입력해주세요.')
            return
        }

        setTemporaryPasswordLoading(true)
        setTemporaryPasswordCopied(false)

        try {
            const { data } = await API.post('/api/v1/admin/appUser/issueTemporaryPassword', {
                userId: id,
                reason,
            })

            if (data.code === 200 && data.data?.temporaryPassword) {
                setTemporaryPassword(data.data.temporaryPassword)
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('임시 비밀번호를 발급했습니다.')
                return
            }

            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(data.message || '임시 비밀번호 발급에 실패했습니다.')
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('임시 비밀번호 발급에 실패했습니다.')
        } finally {
            setTemporaryPasswordLoading(false)
        }
    }

    const openNicknameModerationDialog = () => {
        if (!canModerateNickname || !user.email) {
            return
        }

        setNicknameModerationReason('')
        setNicknameModerationDialogOpen(true)
    }

    const closeNicknameModerationDialog = () => {
        if (nicknameModerationLoading) {
            return
        }

        setNicknameModerationDialogOpen(false)
        setNicknameModerationReason('')
    }

    const replaceInappropriateNickname = async () => {
        const reason = nicknameModerationReason.trim()

        if (!canModerateNickname) {
            return
        }

        if (!reason) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('처리 사유를 입력해주세요.')
            return
        }

        setNicknameModerationLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/appUser/replaceInappropriateNickname', {
                userId: id,
                reason,
            })

            if (data.code === 200 && data.data?.newNickName) {
                const result = data.data
                setNicknameModerationDialogOpen(false)
                setNicknameModerationReason('')
                setUser((currentUser) => ({
                    ...currentUser,
                    nickName: result.newNickName,
                }))
                setNicknameModeration({
                    operationLogId: result.operationLogId,
                    previousNickName: result.previousNickName,
                    newNickName: result.newNickName,
                    changedAt: result.changedAt,
                    mailSent: result.mailSent,
                    mailErrorMessage: result.mailErrorMessage || null,
                })
                setOpenSnackbar(true)
                setSeveritySnackbar(result.mailSent ? 'success' : 'warning')
                setMessageSnackbar(data.message || (result.mailSent
                    ? '닉네임을 변경하고 안내 메일을 발송했습니다.'
                    : '닉네임은 변경했지만 안내 메일 발송에 실패했습니다.'))
                return
            }

            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(data.message || '닉네임 변경에 실패했습니다.')
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('닉네임 변경에 실패했습니다.')
        } finally {
            setNicknameModerationLoading(false)
        }
    }

    const resendNicknameChangeNotice = async () => {
        if (!canModerateNickname || nicknameNoticeResending || !nicknameModeration?.operationLogId) {
            return
        }

        setNicknameNoticeResending(true)

        try {
            const { data } = await API.post('/api/v1/admin/appUser/resendNicknameChangeNotice', {
                userId: id,
                operationLogId: nicknameModeration.operationLogId,
            })

            if (data.code === 200) {
                const mailSent = Boolean(data.data?.mailSent)
                setNicknameModeration((currentModeration) => currentModeration ? {
                    ...currentModeration,
                    mailSent,
                    mailErrorMessage: data.data?.mailErrorMessage || null,
                } : currentModeration)
                setOpenSnackbar(true)
                setSeveritySnackbar(mailSent ? 'success' : 'warning')
                setMessageSnackbar(data.message || (mailSent
                    ? '닉네임 변경 안내 메일을 재발송했습니다.'
                    : '닉네임 변경 안내 메일 재발송에 실패했습니다.'))
                return
            }

            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(data.message || '닉네임 변경 안내 메일 재발송에 실패했습니다.')
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('닉네임 변경 안내 메일 재발송에 실패했습니다.')
        } finally {
            setNicknameNoticeResending(false)
        }
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="회원 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={moveToList} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>회원 상세</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'stretch', sm: 'center' },
                            justifyContent: 'space-between',
                            gap: 1.5,
                            mb: 2,
                        }}
                    >
                        <Title>회원 상세</Title>
                        {(canModerateNickname || canIssueTemporaryPassword) && (
                            <Box
                                role="group"
                                aria-label="회원 관리 작업"
                                sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1 }}
                            >
                                {canModerateNickname && (
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        color="error"
                                        startIcon={<ReportProblemIcon />}
                                        onClick={openNicknameModerationDialog}
                                        disabled={nicknameModerationLoading || nicknameNoticeResending || !user.email}
                                    >
                                        불건전 닉네임 변경
                                    </Button>
                                )}
                                {canIssueTemporaryPassword && (
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        color="warning"
                                        startIcon={<VpnKeyIcon />}
                                        onClick={openTemporaryPasswordDialog}
                                        disabled={temporaryPasswordLoading}
                                    >
                                        임시 비밀번호 발급
                                    </Button>
                                )}
                            </Box>
                        )}
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={DETAIL_CARD_SX}>
                                <DetailSectionTitle icon={<PermIdentity sx={{ fontSize: 20, color: 'primary.main' }} />}>
                                    기본 정보
                                </DetailSectionTitle>
                                <Box role="group" aria-label="회원 식별 정보" sx={DETAIL_FIELD_COLUMN_SX}>
                                    <DetailField icon={<LanguageIcon sx={DETAIL_ICON_SX} />} label="국가" value={user.nameKr} />
                                    <DetailField icon={<WcIcon sx={DETAIL_ICON_SX} />} label="성별" value={user.gender === 'm' ? '남' : user.gender === 'f' ? '여' : '-'} />
                                    <DetailField icon={<PermIdentity sx={DETAIL_ICON_SX} />} label="이름" value={user.username} />
                                    <DetailField icon={<NumbersIcon sx={DETAIL_ICON_SX} />} label="닉네임" value={user.nickName} />
                                    <DetailField icon={<PhoneAndroid sx={DETAIL_ICON_SX} />} label="연락처" value={user.phone} />
                                    <DetailField icon={<MailOutline sx={DETAIL_ICON_SX} />} label="이메일" value={user.email} />
                                </Box>
                                {canModerateNickname && (!user.email || (nicknameModeration && nicknameModeration.mailSent !== true)) && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        {!user.email && (
                                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                                안내 메일을 발송할 이메일이 없어 닉네임을 변경할 수 없습니다.
                                            </Typography>
                                        )}
                                        {nicknameModeration && nicknameModeration.mailSent !== true && (
                                            <Box
                                                sx={{
                                                    p: 1.25,
                                                    border: '1px solid',
                                                    borderColor: 'warning.main',
                                                    borderRadius: 2,
                                                    bgcolor: 'warning.light',
                                                }}
                                            >
                                                <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 700 }}>
                                                    닉네임은 {nicknameModeration.newNickName || user.nickName || '-'}(으)로 변경됐지만 안내 메일 발송이 완료되지 않았습니다.
                                                </Typography>
                                                <Button
                                                    type="button"
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    startIcon={<MailOutline />}
                                                    onClick={resendNicknameChangeNotice}
                                                    disabled={nicknameNoticeResending || nicknameModerationLoading || !user.email}
                                                    sx={{ mt: 1 }}
                                                >
                                                    {nicknameNoticeResending ? '재발송 중' : '안내 메일 재발송'}
                                                </Button>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper variant="outlined" sx={DETAIL_CARD_SX}>
                                <DetailSectionTitle icon={<LoginIcon sx={{ fontSize: 20, color: 'primary.main' }} />}>
                                    계정·결제 정보
                                </DetailSectionTitle>
                                <Box role="group" aria-label="회원 활동 정보" sx={DETAIL_FIELD_COLUMN_SX}>
                                    <DetailField icon={<LoginIcon sx={DETAIL_ICON_SX} />} label="가입 경로" value={getSignupPathLabel(user.type)} />
                                    <DetailField icon={<PermIdentity sx={DETAIL_ICON_SX} />} label="가입방식" value={getSignupProviderLabel(user.signupProvider)} />
                                    <DetailField icon={<CalendarToday sx={DETAIL_ICON_SX} />} label="가입일" value={formatDate(user.regTime)} />
                                    <DetailField icon={<DevicesIcon sx={DETAIL_ICON_SX} />} label="마지막 로그인 기기" value={getLastLoginDeviceLabel(user.deviceType, user.type)} />
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                            gap: 1.25,
                                            p: 1.5,
                                            mt: 0.25,
                                            borderRadius: 2,
                                            bgcolor: 'action.hover',
                                        }}
                                    >
                                        <DetailField icon={<PaymentIcon sx={DETAIL_ICON_SX} />} label="결제건수" value={`${Number(paymentSummary.paymentCount || 0)}건`} />
                                        <DetailField icon={<CalendarToday sx={DETAIL_ICON_SX} />} label="최근 결제일" value={renderDateTime(paymentSummary.lastPaymentAt)} />
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={DETAIL_CARD_SX}>
                                <DetailSectionTitle icon={<DirectionsRunIcon sx={{ fontSize: 20, color: 'primary.main' }} />}>
                                    러닝 요약
                                </DetailSectionTitle>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                                        gap: 1,
                                    }}
                                >
                                    <RunSummaryCard label="총 거리" value={`${(Number(user.totalDistance || 0) / 1000).toFixed(2)} km`} />
                                    <RunSummaryCard label="총 시간" value={formatRunDuration(user.totalTime)} />
                                    <RunSummaryCard label="평균 페이스" value={`${formatRunPace(user.totalPace)} 페이스`} />
                                    <RunSummaryCard label="러닝 횟수" value={`${Number(user.totalCount || 0)} 회`} />
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={DETAIL_CARD_SX}>
                                <DetailSectionTitle icon={<LinkIcon sx={{ fontSize: 20, color: 'primary.main' }} />}>
                                    SNS 연동 이력
                                </DetailSectionTitle>
                                <EmbeddedTableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>연동 방식</TableCell>
                                                <TableCell>Provider ID</TableCell>
                                                <TableCell>중계 이메일</TableCell>
                                                <TableCell>이메일 상태</TableCell>
                                                <TableCell>연동일</TableCell>
                                                <TableCell>수정일</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {snsLinks.length > 0 ? (
                                                snsLinks.map((snsLink) => (
                                                    <TableRow
                                                        key={`${snsLink.provider}:${snsLink.providerId}`}
                                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                                    >
                                                        <TableCell>{getSignupProviderLabel(snsLink.provider)}</TableCell>
                                                        <TableCell sx={{ wordBreak: 'break-all' }}>{snsLink.providerId || '-'}</TableCell>
                                                        <TableCell sx={{ wordBreak: 'break-all' }}>{snsLink.relayEmail || '-'}</TableCell>
                                                        <TableCell>{Number(snsLink.isEmailActive) === 1 ? '활성' : '비활성'}</TableCell>
                                                        <TableCell>{renderDateTime(snsLink.regTime)}</TableCell>
                                                        <TableCell>{renderDateTime(snsLink.updTime)}</TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={6}>
                                                        <ListEmptyState
                                                            title="SNS 연동 이력이 없습니다."
                                                            description="해당 회원에게 저장된 SNS 연동 정보가 없습니다."
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </EmbeddedTableContainer>
                            </Paper>
                        </Grid>
                    </Grid>
                    
                    <BottomButtonGroup onBackClick={moveToList} backLabel="뒤로"/>
                    <Dialog
                        open={nicknameModerationDialogOpen}
                        onClose={nicknameModerationLoading ? undefined : closeNicknameModerationDialog}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>불건전 닉네임 변경</DialogTitle>
                        <DialogContent>
                            <DialogContentText sx={{ mb: 2 }}>
                                현재 닉네임을 Runner로 시작하는 시스템 닉네임으로 변경하고, 등록된 이메일로 국문·영문 안내 메일을 발송합니다.
                            </DialogContentText>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                    gap: 1,
                                    mb: 2,
                                    p: 1.25,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                }}
                            >
                                <DetailField icon={<NumbersIcon sx={DETAIL_ICON_SX} />} label="현재 닉네임" value={user.nickName} />
                                <DetailField icon={<MailOutline sx={DETAIL_ICON_SX} />} label="안내 메일 수신자" value={user.email} />
                            </Box>
                            <TextField
                                autoFocus
                                fullWidth
                                required
                                multiline
                                minRows={3}
                                label="처리 사유"
                                value={nicknameModerationReason}
                                inputProps={{ maxLength: NICKNAME_MODERATION_REASON_MAX_LENGTH }}
                                helperText={`${nicknameModerationReason.length}/${NICKNAME_MODERATION_REASON_MAX_LENGTH} · 내부 감사 로그에만 기록됩니다.`}
                                onChange={(event) => setNicknameModerationReason(event.target.value)}
                                disabled={nicknameModerationLoading}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeNicknameModerationDialog} variant="outlined" color="secondary" disabled={nicknameModerationLoading}>
                                취소
                            </Button>
                            <Button
                                onClick={replaceInappropriateNickname}
                                variant="contained"
                                color="error"
                                disabled={nicknameModerationLoading || !nicknameModerationReason.trim() || !user.email}
                            >
                                {nicknameModerationLoading ? '변경 중' : '변경 및 메일 발송'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog
                        open={temporaryPasswordDialogOpen}
                        onClose={temporaryPasswordLoading ? undefined : closeTemporaryPasswordDialog}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>
                            {temporaryPassword ? '임시 비밀번호 발급 완료' : '임시 비밀번호 발급'}
                        </DialogTitle>
                        <DialogContent>
                            {temporaryPassword ? (
                                <>
                                    <DialogContentText sx={{ mb: 2 }}>
                                        아래 임시 비밀번호는 이 창에서만 확인할 수 있습니다. 사용자에게 전달한 뒤 즉시 비밀번호 변경을 안내해주세요.
                                    </DialogContentText>
                                    <TextField
                                        fullWidth
                                        label="임시 비밀번호"
                                        value={temporaryPassword}
                                        InputProps={{ readOnly: true }}
                                        sx={{ mt: 1 }}
                                    />
                                </>
                            ) : (
                                <>
                                    <DialogContentText sx={{ mb: 2 }}>
                                        {user.nickName || user.email || `userId ${id}`} 유저의 비밀번호를 새 임시 비밀번호로 교체합니다.
                                    </DialogContentText>
                                    <TextField
                                        fullWidth
                                        required
                                        multiline
                                        minRows={3}
                                        label="처리 사유"
                                        value={temporaryPasswordReason}
                                        inputProps={{ maxLength: TEMP_PASSWORD_REASON_MAX_LENGTH }}
                                        helperText={`${temporaryPasswordReason.length}/${TEMP_PASSWORD_REASON_MAX_LENGTH}`}
                                        onChange={(event) => setTemporaryPasswordReason(event.target.value)}
                                        disabled={temporaryPasswordLoading}
                                    />
                                </>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={closeTemporaryPasswordDialog} variant="outlined" color="secondary" disabled={temporaryPasswordLoading}>
                                닫기
                            </Button>
                            {temporaryPassword ? (
                                <Button
                                    onClick={copyTemporaryPassword}
                                    variant="contained"
                                    startIcon={<ContentCopyIcon />}
                                    disabled={temporaryPasswordCopied}
                                >
                                    {temporaryPasswordCopied ? '복사됨' : '복사'}
                                </Button>
                            ) : (
                                <Button
                                    onClick={issueTemporaryPassword}
                                    variant="contained"
                                    color="warning"
                                    disabled={temporaryPasswordLoading || !temporaryPasswordReason.trim()}
                                >
                                    {temporaryPasswordLoading ? '발급 중' : '발급'}
                                </Button>
                            )}
                        </DialogActions>
                    </Dialog>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}     
                </Paper>
        </Grid>
    )
}
